









































































import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import Character from '@/classes/character/character';
import BaseAttributeCollection from '../../classes/attributes/baseAttributeCollection';
import DiceAttribute from '../../classes/attributes/diceAttribute';
import ValueAttribute from '../../classes/attributes/valueAttribute';
import TalentCollection from '../../classes/attributes/talentCollection';
import CharacterDetailValueGroup from './characterDetailValueGroup.vue';
import { Modifier } from '../../classes/character/modifier';
import BaseAttribute from '../../classes/attributes/baseAttribute';
import WeaponAttribute from '../../classes/attributes/weapon';

@Component({ components: { CharacterDetailValueGroup } })
export default class CharacterDetail extends Vue {
  @Prop({ default: true })
  public open!: boolean;

  @Prop()
  public characterId!: string;

  @Prop()
  public name!: string;

  private character: Character | null = null;

  private loading = true;
  private editMode = false;

  private attributeCollection = new BaseAttributeCollection();
  private talentCollection = new TalentCollection();

  @Watch('open')
  private watchDialogOpen(open) {
    if (!open) {
      return;
    }
    if (!this.characterId) {
      throw new Error('No characterId passed');
    }
    this.loading = true;
    this.$store.dispatch('loadCharacter', this.characterId).then((data) => {
      this.character = new Character(data);
      this.loading = false;
    });
  }

  get baseResources() {
    if (this.character === null) {
      return [];
    }

    const resources: BaseAttribute[] = [];
    this.attributeCollection.baseResources.forEach((resource) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      const value = this.character.getFinalValueByAbbreviation(resource.abbreviation, false) as ValueAttribute;
      resources.push(value);
    });
    this.attributeCollection.baseDiceResources.forEach((resource) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      const value = this.character.getFinalValueByAbbreviation(resource.abbreviation, false) as DiceAttribute;
      resources.push(value);
    });
    return resources;
  }

  get baseAttributes() {
    if (this.character === null) {
      return [];
    }
    const attributes: DiceAttribute[] = [];
    this.attributeCollection.baseDiceAttributes.forEach((attribute) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation) as DiceAttribute;
      attributes.push(value);
    });
    return attributes;
  }

  get otherBaseAttributes() {
    if (this.character === null) {
      return [];
    }
    const attributes: ValueAttribute[] = [];
    this.attributeCollection.baseAttributes.forEach((attribute) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation, false) as ValueAttribute;
      attributes.push(value);
    });
    return attributes;
  }

  get elements() {
    if (this.character === null) {
      return [];
    }
    const elements: DiceAttribute[] = [];
    this.attributeCollection.baseElements.forEach((attribute) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      if (attribute.isProtection) {
        return;
      }
      const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation) as DiceAttribute;
      elements.push(value);
    });
    return elements;
  }

  get weaponskills() {
    if (this.character === null) {
      return [];
    }
    const weapons: DiceAttribute[] = [];
    this.attributeCollection.baseCombatDiceAttributes
      .filter((weapon) => !(weapon as WeaponAttribute).isProtection)
      .forEach((attribute) => {
        if (this.character === null) {
          throw new Error('character not supposed to be null');
        }
        const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation) as DiceAttribute;
        weapons.push(value);
      });
    return weapons;
  }

  get weaponskillsDefence() {
    if (this.character === null) {
      return [];
    }
    const weapons: DiceAttribute[] = [];
    this.attributeCollection.baseCombatDiceAttributes
      .filter((weapon) => (weapon as WeaponAttribute).isProtection)
      .forEach((attribute) => {
        if (this.character === null) {
          throw new Error('character not supposed to be null');
        }
        const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation) as DiceAttribute;
        weapons.push(value);
      });
    return weapons;
  }

  get elementsProtection() {
    if (this.character === null) {
      return [];
    }
    const elements: DiceAttribute[] = [];
    this.attributeCollection.baseElements.forEach((attribute) => {
      if (this.character === null) {
        throw new Error('character not supposed to be null');
      }
      if (!attribute.isProtection) {
        return;
      }
      const value = this.character.getFinalValueByAbbreviation(attribute.abbreviation) as DiceAttribute;
      elements.push(value);
    });
    return elements;
  }

  get allTalentFields() {
    return this.talentCollection.getAllTalentFields();
  }

  private getModifiersOfAttributes(attributes: ValueAttribute[]) {
    if (this.character === null) {
      return {};
    }
    const modifierRecord: Record<string, string[]> = {};
    for (const attribute of attributes) {
      const modifiers = this.character.getModifiersOfAttribute(attribute.abbreviation);

      // group them by source
      const modsBySource: Record<string, string> = {};
      for (const mod of modifiers) {
        if (typeof modsBySource[mod.source] === 'undefined') {
          if (mod.attributeValue !== 'value') {
            modsBySource[mod.source] = '0W +0';
          } else {
            modsBySource[mod.source] = mod.value.toString();
          }
        }
        if (mod.attributeValue === 'numDice') {
          modsBySource[mod.source] = modsBySource[mod.source].replace('0W', mod.value.toString() + 'W');
        } else if (mod.attributeValue === 'numBonus') {
          modsBySource[mod.source] = modsBySource[mod.source].replace('+0', '+' + mod.value.toString());
        }
      }
      const results: string[] = [];
      for (const source of Object.keys(modsBySource)) {
        results.push(source + ': ' + modsBySource[source]);
      }
      modifierRecord[attribute.abbreviation] = results;
    }
    return modifierRecord;
  }

  private getTalentModifiers() {
    if (this.character === null) {
      return {};
    }
    const modifierRecord: Record<string, string[]> = {};
    for (const talent of this.talentCollection.allTalents) {
      const modifiers = this.character.getModifiersOfAttribute(talent.abbreviation);
      if (modifiers.length === 0) {
        continue;
      }
      const results: string[] = [];
      modifiers.forEach((mod) => {
        results.push(mod.source + ': ' + mod.value);
      });
      modifierRecord[talent.abbreviation] = results;
    }
    return modifierRecord;
  }
}
